import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { ChevronDownIcon, PlusIcon, CloseIcon } from "./icons";
import { SunIcon, MonitorIcon, MoonIcon } from "../Header/icons";
import { TariffLabel } from "../TariffLabel";
import bgGlow from "./assets/bg-glow.png";
import bgRocket from "./assets/bg-rocket.png";
import { NewOrderSelectAppProps } from "./types";
import { ThemeMode } from "../Header/types";

const THEMES: {
  mode: ThemeMode;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
}[] = [
  { mode: "light", Icon: SunIcon },
  { mode: "system", Icon: MonitorIcon },
  { mode: "dark", Icon: MoonIcon },
];

const SelectField: React.FC<{
  label: string;
  value?: string;
  placeholder?: string;
  onClick?: () => void;
}> = ({ label, value, placeholder = "Select", onClick }) => (
  <div className={styles.field}>
    <span className={styles.fieldLabel}>{label}</span>
    <button type="button" className={styles.fieldControl} onClick={onClick}>
      <span className={cs(styles.fieldValue, { [styles.placeholder]: !value })}>
        {value ?? placeholder}
      </span>
      <ChevronDownIcon className={styles.icon20} />
    </button>
  </div>
);

/**
 * New-order "Select an app" step (Figma 560:102458). Full-width hero screen
 * for step 1 of the New Order flow: title + order-type tag, a 2-step progress
 * pill, balance chip + close button over a decorative illustration, then the
 * Select-app / Select-country fields and Back/Continue actions. Pure
 * presentational — data + handlers come from props; app/country pickers can
 * be swapped for real, interactive components via slots.
 */
export const NewOrderSelectApp: React.FC<NewOrderSelectAppProps> = ({
  orderType,
  step = "select-app",
  tariffLabel,
  tariffLevel = "trial",
  tariffMeta,
  amount,
  onAddFund,
  theme = "light",
  onThemeChange,
  onClose,
  sectionTitle = "Select an app",
  appValue,
  appPlaceholder = "Select app",
  onAppClick,
  countryValue,
  countryPlaceholder = "Select country",
  onCountryClick,
  continueDisabled = true,
  onBack,
  onContinue,
  appSelectSlot,
  countrySelectSlot,
  className,
}) => {
  const isSelectApp = step === "select-app";

  return (
    <div className={cs(styles.card, className)}>
      <div className={styles.hero}>
        <img className={styles.bgGlow} src={bgGlow} alt="" aria-hidden />
        <img className={styles.bgRocket} src={bgRocket} alt="" aria-hidden />

        <div className={styles.topRow}>
          <div className={styles.titleGroup}>
            <h1 className={styles.title}>New order</h1>
            <span className={styles.orderType}>{orderType}</span>
          </div>

          <div className={styles.stepper} role="group" aria-label="Progress">
            <span
              className={cs(styles.step, { [styles.stepActive]: isSelectApp })}
            >
              <span
                className={cs(styles.stepDot, {
                  [styles.stepDotIncomplete]: !isSelectApp,
                })}
                aria-hidden
              />
              Select an app
            </span>
            <span
              className={cs(styles.step, { [styles.stepActive]: !isSelectApp })}
            >
              <span
                className={cs(styles.stepDot, {
                  [styles.stepDotIncomplete]: isSelectApp,
                })}
                aria-hidden
              />
              Configure
            </span>
          </div>

          <div className={styles.rightCluster}>
            <div className={styles.walletGroup}>
              <div className={styles.wallet}>
                <TariffLabel
                  level={tariffLevel}
                  label={tariffLabel}
                  meta={tariffMeta ?? ""}
                />

                <span className={styles.amount}>
                  <span className={styles.amountCurrency}>
                    {amount.currency ?? "$"}
                  </span>
                  <span className={styles.amountWhole}>{amount.whole}</span>
                  <span className={styles.amountFraction}>
                    .{amount.fraction}
                  </span>
                </span>

                <button
                  type="button"
                  className={styles.addFund}
                  onClick={onAddFund}
                  aria-label="Add funds"
                >
                  <PlusIcon />
                </button>
              </div>

              <div
                className={styles.themeSwitch}
                role="group"
                aria-label="Theme"
              >
                {THEMES.map(({ mode, Icon }) => (
                  <button
                    key={mode}
                    type="button"
                    className={cs(styles.switchItem, {
                      [styles.active]: theme === mode,
                    })}
                    onClick={() => onThemeChange?.(mode)}
                    aria-pressed={theme === mode}
                    aria-label={mode}
                  >
                    <Icon />
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              className={styles.close}
              onClick={onClose}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className={styles.divider} />
      </div>

      <div className={styles.body}>
        <h3 className={styles.sectionTitle}>{sectionTitle}</h3>

        <div className={styles.fields}>
          {appSelectSlot ?? (
            <SelectField
              label="Select app"
              value={appValue}
              placeholder={appPlaceholder}
              onClick={onAppClick}
            />
          )}
          {countrySelectSlot ?? (
            <SelectField
              label="Select country"
              value={countryValue}
              placeholder={countryPlaceholder}
              onClick={onCountryClick}
            />
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.backButton} onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className={styles.continueButton}
          disabled={continueDisabled}
          onClick={onContinue}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default NewOrderSelectApp;
